import React, { useState, useEffect, useCallback } from 'react';
import { FileCheck, Plus, Edit2, Trash2, X, Calendar, Euro, Phone, Mail } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const typeContrats = [
  { value: 'ascenseur', label: 'Ascenseur' },
  { value: 'chauffage', label: 'Chauffage' },
  { value: 'nettoyage', label: 'Nettoyage' },
  { value: 'espaces_verts', label: 'Espaces verts' },
  { value: 'securite', label: 'Sécurité / Gardiennage' },
  { value: 'extincteurs', label: 'Extincteurs' },
  { value: 'desinfection', label: 'Désinfection / 3D' },
  { value: 'portail', label: 'Portail automatique' },
  { value: 'autre', label: 'Autre' },
];

export default function ContratsTab({ buildingId, token }) {
  const [contrats, setContrats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({});

  const fetchContrats = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/carnet-entretien/contrats/${buildingId}`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (response.ok) setContrats(await response.json());
    } catch (error) { console.error('Error:', error); }
    finally { setLoading(false); }
  }, [buildingId, token]);

  useEffect(() => { fetchContrats(); }, [fetchContrats]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const url = editingId ? `${API_URL}/api/carnet-entretien/contrats/${editingId}` : `${API_URL}/api/carnet-entretien/contrats`;
    const method = editingId ? 'PUT' : 'POST';
    try {
      const response = await fetch(url, {
        method, headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, building_id: buildingId })
      });
      if (response.ok) {
        toast.success(editingId ? 'Contrat modifié' : 'Contrat ajouté');
        setShowModal(false); setEditingId(null); setForm({});
        fetchContrats();
      }
    } catch (error) { toast.error('Erreur'); }
  };

  const openNew = () => { setForm({ type_contrat: 'autre', statut: 'actif' }); setEditingId(null); setShowModal(true); };
  const openEdit = (c) => { setForm(c); setEditingId(c.id); setShowModal(true); };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '-';
  const formatMoney = (a) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(a || 0);

  const isExpiringSoon = (dateFin) => {
    if (!dateFin) return false;
    const d = new Date(dateFin);
    const now = new Date();
    const diff = (d - now) / (1000 * 60 * 60 * 24);
    return diff > 0 && diff < 60;
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00D3A8]"></div></div>;

  const actifs = contrats.filter(c => c.statut === 'actif');
  const expires = contrats.filter(c => c.statut !== 'actif');

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Contrats de maintenance ({actifs.length} actifs)</h2>
        <button onClick={openNew} className="flex items-center gap-2 px-4 py-2 bg-[#00D3A8] text-white rounded-lg hover:bg-[#00B892]">
          <Plus className="h-5 w-5" />Nouveau contrat
        </button>
      </div>

      {contrats.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-xl">
          <FileCheck className="h-12 w-12 mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500">Aucun contrat enregistré</p>
        </div>
      ) : (
        <div className="space-y-4">
          {actifs.map(c => {
            const type = typeContrats.find(t => t.value === c.type_contrat) || typeContrats[typeContrats.length-1];
            const expiring = isExpiringSoon(c.date_fin);
            return (
              <div key={c.id} className={`bg-white rounded-xl border ${expiring ? 'border-orange-300 bg-orange-50/30' : 'border-gray-200'} p-5`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${c.statut === 'actif' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>{c.statut === 'actif' ? 'Actif' : 'Expiré'}</span>
                      {expiring && <span className="px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-700">Expire bientôt</span>}
                    </div>
                    <h3 className="font-semibold text-gray-900 mt-2">{type.label}</h3>
                    <p className="text-gray-600">{c.prestataire}</p>
                  </div>
                  <button onClick={() => openEdit(c)} className="p-2 hover:bg-gray-100 rounded-lg"><Edit2 className="h-4 w-4 text-gray-500" /></button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 pt-4 border-t border-gray-100">
                  <div><p className="text-xs text-gray-500">Période</p><p className="text-sm font-medium">{formatDate(c.date_debut)} → {formatDate(c.date_fin)}</p></div>
                  <div><p className="text-xs text-gray-500">Montant annuel</p><p className="text-sm font-medium">{formatMoney(c.montant_annuel)}</p></div>
                  {c.prestataire_tel && <div><p className="text-xs text-gray-500">Téléphone</p><p className="text-sm font-medium flex items-center gap-1"><Phone className="h-3 w-3" />{c.prestataire_tel}</p></div>}
                  {c.prestataire_email && <div><p className="text-xs text-gray-500">Email</p><p className="text-sm font-medium flex items-center gap-1"><Mail className="h-3 w-3" />{c.prestataire_email}</p></div>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b flex justify-between items-center sticky top-0 bg-white">
              <h3 className="font-semibold">{editingId ? 'Modifier' : 'Nouveau'} contrat</h3>
              <button onClick={() => setShowModal(false)}><X className="h-5 w-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div><label className="block text-sm font-medium mb-1">Type de contrat *</label>
                <select value={form.type_contrat || ''} onChange={(e) => setForm({...form, type_contrat: e.target.value})} required className="w-full px-3 py-2 border rounded-lg">
                  {typeContrats.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div><label className="block text-sm font-medium mb-1">Prestataire *</label><input type="text" value={form.prestataire || ''} onChange={(e) => setForm({...form, prestataire: e.target.value})} required className="w-full px-3 py-2 border rounded-lg" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-sm font-medium mb-1">Téléphone</label><input type="tel" value={form.prestataire_tel || ''} onChange={(e) => setForm({...form, prestataire_tel: e.target.value})} className="w-full px-3 py-2 border rounded-lg" /></div>
                <div><label className="block text-sm font-medium mb-1">Email</label><input type="email" value={form.prestataire_email || ''} onChange={(e) => setForm({...form, prestataire_email: e.target.value})} className="w-full px-3 py-2 border rounded-lg" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-sm font-medium mb-1">Date début *</label><input type="date" value={form.date_debut || ''} onChange={(e) => setForm({...form, date_debut: e.target.value})} required className="w-full px-3 py-2 border rounded-lg" /></div>
                <div><label className="block text-sm font-medium mb-1">Date fin *</label><input type="date" value={form.date_fin || ''} onChange={(e) => setForm({...form, date_fin: e.target.value})} required className="w-full px-3 py-2 border rounded-lg" /></div>
              </div>
              <div><label className="block text-sm font-medium mb-1">Montant annuel (€)</label><input type="number" value={form.montant_annuel || ''} onChange={(e) => setForm({...form, montant_annuel: parseFloat(e.target.value) || 0})} className="w-full px-3 py-2 border rounded-lg" /></div>
              <div><label className="block text-sm font-medium mb-1">Fréquence des interventions</label>
                <select value={form.frequence_intervention || ''} onChange={(e) => setForm({...form, frequence_intervention: e.target.value})} className="w-full px-3 py-2 border rounded-lg">
                  <option value="">Non spécifié</option>
                  <option value="mensuel">Mensuel</option>
                  <option value="trimestriel">Trimestriel</option>
                  <option value="semestriel">Semestriel</option>
                  <option value="annuel">Annuel</option>
                  <option value="sur_appel">Sur appel uniquement</option>
                </select>
              </div>
              <div><label className="block text-sm font-medium mb-1">Périmètre couvert / Équipements concernés</label><textarea value={form.perimetre_couvert || ''} onChange={(e) => setForm({...form, perimetre_couvert: e.target.value})} rows="2" className="w-full px-3 py-2 border rounded-lg" placeholder="Ex: Ascenseur A et B, VMC hall d'entrée..." /></div>
              <div><label className="block text-sm font-medium mb-1">Description / Prestations incluses</label><textarea value={form.description || ''} onChange={(e) => setForm({...form, description: e.target.value})} rows="2" className="w-full px-3 py-2 border rounded-lg" placeholder="Détail des prestations..." /></div>
              <div><label className="block text-sm font-medium mb-1">Numéro de contrat</label><input type="text" value={form.numero_contrat || ''} onChange={(e) => setForm({...form, numero_contrat: e.target.value})} className="w-full px-3 py-2 border rounded-lg" placeholder="Référence du contrat" /></div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">Annuler</button>
                <button type="submit" className="px-4 py-2 bg-[#00D3A8] text-white rounded-lg hover:bg-[#00B892]">{editingId ? 'Modifier' : 'Créer'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
